// Minimal WHIP (WebRTC-HTTP Ingestion Protocol, draft-ietf-wish-whip) client.
//
// Flow:
//   1. POST gathered SDP offer to gateway with bearer token
//   2. Gateway responds 201 with SDP answer + Location header (resource URL)
//   3. To stop, DELETE the resource URL

export type WhipSession = {
  pc: RTCPeerConnection;
  resourceUrl: string;
  stop: () => Promise<void>;
  replaceVideoTrack: (track: MediaStreamTrack) => Promise<void>;
  replaceAudioTrack: (track: MediaStreamTrack) => Promise<void>;
  updateBitrates: (videoBitrateKbps: number, audioBitrateKbps: number) => Promise<void>;
};

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

export type WhipGeoFix = {
  lat: number;
  lon: number;
  accuracyM: number;
  at: number;
};

export async function startWhip(opts: {
  endpoint: string;
  token: string;
  stream: MediaStream;
  geo: WhipGeoFix;
  broadcastId?: string;
  transcribeLanguage?: string;
  simulcast?: string[];
  streamTitle?: string;
  videoBitrateKbps?: number;
  audioBitrateKbps?: number;
  onIceState?: (s: RTCIceConnectionState) => void;
}): Promise<WhipSession> {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, bundlePolicy: "max-bundle" });
  opts.onIceState?.(pc.iceConnectionState);
  pc.oniceconnectionstatechange = () => opts.onIceState?.(pc.iceConnectionState);

  const tracks = [...opts.stream.getTracks()];
  let silentAudioContext: AudioContext | null = null;
  let silentAudioTrack: MediaStreamTrack | null = null;

  if (opts.stream.getAudioTracks().length === 0 && typeof window !== "undefined" && window.AudioContext) {
    try {
      silentAudioContext = new AudioContext();
      const oscillator = silentAudioContext.createOscillator();
      const gain = silentAudioContext.createGain();
      const destination = silentAudioContext.createMediaStreamDestination();
      gain.gain.value = 0;
      oscillator.connect(gain);
      gain.connect(destination);
      oscillator.start();
      silentAudioTrack = destination.stream.getAudioTracks()[0] ?? null;
      if (silentAudioTrack) {
        tracks.push(silentAudioTrack);
      }
    } catch (e) {
      console.warn("silent audio track setup failed", e);
    }
  }

  const publishStream = new MediaStream(tracks);

  for (const track of tracks) {
    const transceiver = pc.addTransceiver(track, { direction: "sendonly", streams: [publishStream] });
    if (track.kind === "video" && typeof transceiver.setCodecPreferences === "function" && typeof RTCRtpSender.getCapabilities === "function") {
      const codecs = RTCRtpSender.getCapabilities("video")?.codecs ?? [];
      const h264 = codecs.filter((codec) => codec.mimeType.toLowerCase() === "video/h264");
      if (h264.length > 0) {
        transceiver.setCodecPreferences([...h264, ...codecs.filter((codec) => codec.mimeType.toLowerCase() !== "video/h264")]);
      }
    }
    if (track.kind === "audio" && typeof transceiver.setCodecPreferences === "function" && typeof RTCRtpSender.getCapabilities === "function") {
      const codecs = RTCRtpSender.getCapabilities("audio")?.codecs ?? [];
      const opus = codecs.filter((codec) => codec.mimeType.toLowerCase() === "audio/opus");
      if (opus.length > 0) {
        transceiver.setCodecPreferences([...opus, ...codecs.filter((codec) => codec.mimeType.toLowerCase() !== "audio/opus")]);
      }
    }
  }

  const videoSender = pc.getSenders().find((s) => s.track?.kind === "video");
  const audioSender = pc.getSenders().find((s) => s.track?.kind === "audio");
  const applyBitrates = async (videoBitrateKbps?: number, audioBitrateKbps?: number) => {
    if (videoSender && typeof videoSender.setParameters === "function") {
      try {
        const params = videoSender.getParameters();
        params.degradationPreference = "maintain-framerate";
        params.encodings = params.encodings && params.encodings.length > 0 ? params.encodings : [{}];
        if (typeof videoBitrateKbps === "number" && videoBitrateKbps > 0) {
          params.encodings[0].maxBitrate = videoBitrateKbps * 1000;
        }
        await videoSender.setParameters(params);
      } catch (e) {
        console.warn("video sender bitrate tuning failed", e);
      }
    }

    if (audioSender && typeof audioSender.setParameters === "function") {
      try {
        const params = audioSender.getParameters();
        params.encodings = params.encodings && params.encodings.length > 0 ? params.encodings : [{}];
        if (typeof audioBitrateKbps === "number" && audioBitrateKbps > 0) {
          params.encodings[0].maxBitrate = audioBitrateKbps * 1000;
        }
        await audioSender.setParameters(params);
      } catch (e) {
        console.warn("audio sender bitrate tuning failed", e);
      }
    }
  };

  await applyBitrates(opts.videoBitrateKbps, opts.audioBitrateKbps);

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await iceGatheringComplete(pc);

  const localSdp = pc.localDescription?.sdp;
  if (!localSdp) throw new Error("Local SDP not ready");

  const res = await fetch(opts.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/sdp",
      Authorization: `Bearer ${opts.token}`,
      ...(opts.broadcastId ? { "X-Broadcast-Id": opts.broadcastId } : {}),
      ...(opts.transcribeLanguage ? { "X-Transcribe-Language": opts.transcribeLanguage } : {}),
      ...(opts.simulcast && opts.simulcast.length ? { "X-Simulcast": opts.simulcast.join(",") } : {}),
      ...(opts.streamTitle ? { "X-Stream-Title": encodeURIComponent(opts.streamTitle.slice(0, 120)) } : {}),
      "X-Geo-Lat": opts.geo.lat.toFixed(6),
      "X-Geo-Lon": opts.geo.lon.toFixed(6),
      "X-Geo-Accuracy-M": opts.geo.accuracyM.toFixed(1),
      "X-Geo-At": String(opts.geo.at),
    },
    body: localSdp,
  });
  if (!res.ok) {
    pc.close();
    throw new Error(`WHIP ingest rejected (${res.status}): ${await res.text()}`);
  }

  const answer = await res.text();
  const resourceUrl = new URL(res.headers.get("Location") ?? "", opts.endpoint).toString();

  await pc.setRemoteDescription({ type: "answer", sdp: answer });

  const stop = async () => {
    try {
      await fetch(resourceUrl, { method: "DELETE", headers: { Authorization: `Bearer ${opts.token}` } });
    } catch {
      // best-effort
    }
    pc.getSenders().forEach((s) => s.track?.stop());
    silentAudioTrack?.stop();
    if (silentAudioContext) {
      await silentAudioContext.close().catch(() => {});
    }
    pc.close();
  };

  const findSender = (kind: "video" | "audio") =>
    pc.getSenders().find((s) => s.track?.kind === kind) ?? null;

  const replaceVideoTrack = async (track: MediaStreamTrack) => {
    const s = findSender("video");
    if (s) await s.replaceTrack(track);
  };
  const replaceAudioTrack = async (track: MediaStreamTrack) => {
    const s = findSender("audio");
    if (s) await s.replaceTrack(track);
  };
  const updateBitrates = async (videoBitrateKbps: number, audioBitrateKbps: number) => {
    await applyBitrates(videoBitrateKbps, audioBitrateKbps);
  };

  return { pc, resourceUrl, stop, replaceVideoTrack, replaceAudioTrack, updateBitrates };
}

function iceGatheringComplete(pc: RTCPeerConnection, timeoutMs = 10000): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    const onChange = () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(timer);
        pc.removeEventListener("icegatheringstatechange", onChange);
        resolve();
      }
    };
    pc.addEventListener("icegatheringstatechange", onChange);
  });
}
